/**
 * ProjectSourcesView — `/projects/$projectId/sources`
 *
 * Tab "Fuentes" del proyecto. Render delgado: el componente
 * `SourcesPanel` encapsula upload/list/download/archive/delete. Aquí
 * solo armamos el header del tab + la lista de phaseOptions desde el
 * lifecycle del proyecto para el combo.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { fetchProjectLifecycle } from '../../api/projects'
import { listProjectMembers } from '../../api/project_members'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { useProjectAccess } from '../../hooks/useProjectAccess'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { SourcesPanel } from '../../components/sources/SourcesPanel'
import type { PhaseOption } from '../../components/sources/UploadSourceDialog'
import { canManageProjectSources, isExternal } from '../../lib/tiers'
import { useBreadcrumbExtension, useProjectContext } from './ProjectShell'

export function ProjectSourcesView() {
  const { t } = useTranslation(['sources', 'projects'])
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()
  const { me, isLoading: meIsLoading } = useMe()

  useBreadcrumbExtension([{ label: t('projects:shell.tabs.sources') }])

  const lifecycleQuery = useQuery({
    queryKey: ['projects', project.id, 'lifecycle'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchProjectLifecycle(accessToken, project.id)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
  })

  // OT-0009 §1.2 #9 — mismo queryKey que otras vistas del proyecto;
  // TanStack Query dedupe si ya está en cache. M-C: mismo gate que los
  // demás consumidores de este queryKey — `external` no lista members
  // (F6-1: la query solo generaba un 404 de más) y `!meIsLoading` evita
  // la carrera con `useMe()`.
  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken && !meIsLoading && !isExternal(me?.role),
    staleTime: 30_000,
  })
  const { isMember, section } = useProjectAccess(project, membersQuery.data ?? [])
  const canManage = canManageProjectSources(me?.role, me?.tenant?.access, { isMember, section })

  // Phase options para el combo del UploadDialog y el filtro.
  // Solo phases NO skipped (no tiene sentido subir sources a una phase
  // que el cliente no contrató).
  const phaseOptions: PhaseOption[] = useMemo(() => {
    const phases = lifecycleQuery.data?.phases ?? []
    return phases
      .filter((p) => p.contract_mode !== 'skipped')
      .sort((a, b) => a.phase_order - b.phase_order)
      .map((p) => ({
        id: p.id,
        label: p.name || p.phase_code,
      }))
  }, [lifecycleQuery.data])

  return (
    <ScrollPage padding="tight">
      <div className="max-w-7xl">
        <header className="mb-6">
          <h2 className="font-display text-2xl font-semibold text-ink">
            {t('project_view.title')}
          </h2>
          <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
            {t('project_view.subtitle')}
          </p>
        </header>

        <SourcesPanel
          mode="project"
          projectId={project.id}
          phaseOptions={phaseOptions}
          canManage={canManage}
        />
      </div>
    </ScrollPage>
  )
}
